import { useState } from 'react';

export const validationRules = {
  required: value => {
    if (value === null || value === undefined || value === '') {
      return 'Bu alan gereklidir';
    }
    return null;
  },

  email: value => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return 'Geçerli bir e-posta adresi girin';
    }
    return null;
  },

  minLength: min => value => {
    if (value && value.length < min) {
      return `En az ${min} karakter olmalıdır`;
    }
    return null;
  },

  maxLength: max => value => {
    if (value && value.length > max) {
      return `En fazla ${max} karakter olmalıdır`;
    }
    return null;
  },

  password: value => {
    if (!value || value.length < 6) {
      return 'Şifre en az 6 karakter olmalıdır';
    }
    if (!/[A-Z]/.test(value)) {
      return 'Şifre en az bir büyük harf içermelidir';
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)) {
      return 'Şifre en az bir noktalama işareti içermelidir';
    }
    return null;
  },

  passwordLogin: value => {
    if (!value || value.length < 6) {
      return 'Şifre en az 6 karakter olmalıdır';
    }
    return null;
  },

  phone: value => {
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
    if (value && !phoneRegex.test(value)) {
      return 'Geçerli bir telefon numarası girin';
    }
    return null;
  },

  url: value => {
    try {
      new URL(value);
      return null;
    } catch {
      return 'Geçerli bir URL girin';
    }
  },
};

export const validationSchemas = {
  login: {
    email: [validationRules.required, validationRules.email],
    password: [validationRules.required, validationRules.passwordLogin],
  },
  resetPassword: {
    oldPassword: [validationRules.required],
    newPassword: [validationRules.required, validationRules.password],
    confirmPassword: [
      validationRules.required,
      (value, formData) => {
        if (formData && formData.newPassword && value !== formData.newPassword) {
          return 'Şifreler eşleşmiyor';
        }
        return null;
      },
    ],
  },
  forgotPassword: {
    email: [validationRules.required, validationRules.email],
  },
};

export const validateField = (value, rules, formData = null) => {
  if (!rules || !Array.isArray(rules)) return null;

  for (const rule of rules) {
    const error = typeof rule === 'function' ? rule(value, formData) : rule(value);
    if (error) return error;
  }
  return null;
};

export const validateForm = (data, schema) => {
  const errors = {};
  let hasErrors = false;

  Object.keys(schema).forEach(fieldName => {
    const fieldValue = data[fieldName];
    const fieldRules = schema[fieldName];
    const error = validateField(fieldValue, fieldRules, data);

    if (error) {
      errors[fieldName] = error;
      hasErrors = true;
    }
  });

  return { errors, isValid: !hasErrors };
};

export const useFormValidation = (initialData, schema) => {
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateSingleField = (fieldName, value) => {
    const fieldRules = schema[fieldName];
    if (!fieldRules) return null;

    return validateField(value, fieldRules, data);
  };

  const handleChange = (fieldName, value) => {
    setData(prev => ({ ...prev, [fieldName]: value }));

    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: null }));
    }
  };

  const handleBlur = fieldName => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));

    const error = validateSingleField(fieldName, data[fieldName]);
    setErrors(prev => ({ ...prev, [fieldName]: error }));
  };

  const validateAll = () => {
    const { errors: formErrors, isValid } = validateForm(data, schema);
    setErrors(formErrors);
    setTouched(
      Object.keys(schema).reduce((acc, key) => ({ ...acc, [key]: true }), {})
    );
    return { errors: formErrors, isValid };
  };

  const reset = (newData = initialData) => {
    setData(newData);
    setErrors({});
    setTouched({});
  };

  return {
    data,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateAll,
    reset,
    isValid: Object.keys(errors).every(key => !errors[key]),
  };
};
